import { useState, useEffect, type ReactNode } from 'react';

import { useAuth } from 'contexts/auth';
import { checkAdminAccess, checkCuratorAccess } from 'contexts/auth/auth.utils';
import { useEmbedded } from 'contexts/ui/embedded-context';
import loadPendo from 'lib/pendo/loadPendo';

import type { CompanyType, UserType } from 'api/api.types';

type PendoInstalled = {
  pendo?: PendoType;
};

type PendoType = {
  visitorId?: string;
  initialize: (obj: {
    visitor: PendoUserData;
    account: PendoCompanyData;
    disableGuides: boolean;
  }) => void;
  identify: (obj: {
    visitor: PendoUserData;
    account: PendoCompanyData;
  }) => void;
};

type PendoUserData = {
  id: number;
  name: string;
  email: string;
  roles: string[];
  isAdmin: boolean;
  isCurator: boolean;
  isStaff: boolean;
  isInternal: boolean;
  isImpersonating: boolean;
  emailDigest: boolean;
  createdAt: string;
  lastSeenAt: string;
};

type PendoCompanyData = {
  id: number;
  name: string;
  url: string;
  createdAt: string;
  reviewFrequency: string;
};

const buildPendoVisitor = (user: UserType): PendoUserData => {
  const {
    id,
    name,
    email,
    roles,
    isStaff,
    isInternal,
    isImpersonating,
    emailDigest,
    createdAt,
    lastSeenAt,
  } = user;

  return {
    id,
    name,
    email,
    roles,
    isAdmin: checkAdminAccess(user, null),
    isCurator: checkCuratorAccess(user, null),
    isStaff,
    isInternal,
    isImpersonating,
    emailDigest,
    createdAt,
    lastSeenAt,
  };
};

const buildPendoAccount = (company: CompanyType): PendoCompanyData => {
  const { id, name, url, createdAt, reviewFrequency } = company;

  return {
    id,
    name,
    url,
    createdAt,
    reviewFrequency,
  };
};

function PendoEnabled({
  user,
  company,
  disableGuides,
}: {
  user: UserType;
  company: CompanyType;
  disableGuides: boolean;
}): null {
  useEffect(() => {
    const { pendo } = window as PendoInstalled;
    if (!pendo) return;

    const visitor = buildPendoVisitor(user);
    const account = buildPendoAccount(company);

    if (!pendo.visitorId) {
      pendo.initialize({ visitor, account, disableGuides });
    } else {
      pendo.identify({ visitor, account });
    }
  }, [user, company, disableGuides]);

  return null;
}

const Pendo = ({ apiKey }: { apiKey: string }): ReactNode => {
  const { user, company } = useAuth();
  const isEmbedded = useEmbedded();
  const isInsideIframe = window.top !== window.self;
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!user?.id || !company?.id || !apiKey || isLoaded) return;

    loadPendo(apiKey);
    setIsLoaded(true);
  }, [apiKey, company?.id, isLoaded, user?.id]);

  if (!isLoaded || !user || !company) {
    return null;
  }

  return (
    <PendoEnabled
      user={user}
      company={company}
      disableGuides={isEmbedded || isInsideIframe}
    />
  );
};

export default Pendo;
